import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { useTheme } from '../theme';
import { THEMES } from '../theme';
import { apiUrl } from '../api';

export default function SiteSettings() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { themeKey, setThemeKey, tokens, festiveTheme, setFestiveTheme, squareTiles, setSquareTiles } = useTheme();
  const [password, setPassword] = useState('');
  const [unlocked, setUnlocked] = useState(() => {
    try { return sessionStorage.getItem('settingsUnlocked') === '1'; } catch { return false; }
  });
  const [error, setError] = useState('');
  const [saveStatus, setSaveStatus] = useState('');
  
  // Additional site info settings - MUST be declared before any conditional returns
  const [siteInfo, setSiteInfo] = useState(() => {
    try { return JSON.parse(localStorage.getItem('siteInfo') || '{}'); } catch { return {}; }
  });
  
  const themeList = useMemo(() => [
    THEMES.darkNeon,
    THEMES.redLight,
    THEMES.blueLight,
    THEMES.purpleSlate,
    THEMES.tealGray,
  ], []);

  // Fetch siteInfo from server on mount
  useEffect(() => {
    if (unlocked) {
      fetch(apiUrl('/api/settings'))
        .then(res => res.json())
        .then(data => {
          if (data.siteInfo) {
            setSiteInfo(data.siteInfo);
            try { localStorage.setItem('siteInfo', JSON.stringify(data.siteInfo)); } catch {}
          }
        })
        .catch(err => console.error('Failed to fetch settings:', err));
    }
  }, [unlocked]);

  const updateField = (k, v) => setSiteInfo(prev => ({ ...prev, [k]: v }));
  
  const saveSiteInfo = async () => {
    const confirmed = window.confirm(
      'Vrei să salvezi informațiile de contact pentru toate dispozitivele?\n\n' +
      'Modificările vor fi vizibile în footer și pe butoanele mobile după refresh.'
    );
    
    if (!confirmed) return;
    
    try {
      setSaveStatus('Salvează...');
      localStorage.setItem('siteInfo', JSON.stringify(siteInfo));
      
      const response = await fetch(apiUrl('/api/settings'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({ siteInfo })
      });
      
      if (response.ok) {
        setSaveStatus('✓ Salvat cu succes pe toate dispozitivele!');
        setTimeout(() => setSaveStatus(''), 3000);
      } else {
        setSaveStatus('Eroare la salvare');
      }
    } catch (err) {
      console.error('Save error:', err);
      setSaveStatus('Eroare la salvare');
    }
  };
  
  const saveTheme = async (newTheme) => {
    const confirmed = window.confirm(
      `Vrei să aplici tema "${THEMES[newTheme]?.label || newTheme}" pentru toate dispozitivele?\n\n` +
      'Modificarea va fi vizibilă pentru toți utilizatorii după refresh.'
    );
    
    if (!confirmed) return;
    
    try {
      const response = await fetch(apiUrl('/api/settings'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({ themeKey: newTheme })
      });
      
      if (response.ok) {
        // Update local state immediately for instant feedback
        setThemeKey(newTheme);
        alert('✓ Tema a fost salvată cu succes!\n\nCelelalte dispozitive vor vedea schimbarea instant (SSE) sau după refresh (F5).');
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Server rejected theme change:', response.status, errorData);
        const errorMsg = errorData.details || errorData.error || errorData.message || 'eroare necunoscută';
        alert(`⚠ Eroare la salvarea temei pe server (${response.status}):\n${errorMsg}\n\nVerifică consola serverului pentru detalii.`);
      }
    } catch (err) {
      console.error('Failed to save theme to server:', err);
      alert('⚠ Nu s-a putut conecta la server pentru a salva tema.');
    }
  };

  const saveDisplayOptions = async (changes) => {
    const confirmed = window.confirm('Aplici modificările de afișare (festivitate / formă tile) pentru toate dispozitivele?');
    if (!confirmed) return;
    try {
      const body = {};
      if (changes.festiveTheme !== undefined) body.festiveTheme = changes.festiveTheme;
      if (changes.squareTiles !== undefined) body.squareTiles = changes.squareTiles;
      
      const response = await fetch(apiUrl('/api/settings'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-auth-token': token },
        body: JSON.stringify(body)
      });
      
      if (response.ok) {
        // Update local state immediately for instant feedback
        if (changes.festiveTheme !== undefined) setFestiveTheme(changes.festiveTheme);
        if (changes.squareTiles !== undefined) setSquareTiles(changes.squareTiles);
        alert('✓ Modificările au fost propagate. Celelalte browsere se actualizează instant (SSE) sau în câteva secunde.');
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Server rejected display options:', response.status, errorData);
        const errorMsg = errorData.details || errorData.error || errorData.message || 'eroare necunoscută';
        alert(`⚠ Eroare la salvarea opțiunilor pe server (${response.status}):\n${errorMsg}\n\nVerifică consola serverului pentru detalii.`);
      }
    } catch (e) {
      console.error('Failed to save display options:', e);
      alert('⚠ Nu s-a putut conecta la server pentru a salva opțiunile.');
    }
  };

  const onUnlock = (e) => {
    e.preventDefault();
    if ((password || '').trim() === 'stefan') {
      setUnlocked(true);
      setError('');
      try { sessionStorage.setItem('settingsUnlocked', '1'); } catch {}
    } else {
      setError('Parolă incorectă.');
    }
  };

  // Conditional rendering AFTER all hooks
  if (!token) {
    // Not logged in; soft redirect
    return (
      <div style={{ maxWidth: 560, margin: '32px auto', background: tokens.surface, borderRadius: 16, boxShadow: `0 4px 24px ${tokens.shadow}`, padding: 24, color: tokens.text }}>
        <h2 style={{ marginTop: 0 }}>Setări site</h2>
        <p>Trebuie să fii autentificat pentru a accesa această secțiune.</p>
        <button onClick={() => navigate('/login')} style={{ background: tokens.accent, color: tokens.inverse, border: 'none', borderRadius: 8, padding: '10px 16px', fontWeight: 700, cursor: 'pointer' }}>Autentificare</button>
      </div>
    );
  }

  if (!unlocked) {
    return (
      <form onSubmit={onUnlock} style={{ maxWidth: 420, margin: '32px auto', background: tokens.surface, borderRadius: 16, boxShadow: `0 4px 24px ${tokens.shadow}`, padding: 24, display: 'flex', flexDirection: 'column', gap: 12, color: tokens.text }}>
        <h2 style={{ marginTop: 0 }}>Setări site</h2>
        <p>Introduceți parola pentru a accesa setările.</p>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Parolă" required style={{ background: tokens.panel, color: tokens.text, border: `1px solid ${tokens.mutedBorder}`, borderRadius: 8, padding: '10px 12px', fontSize: 16 }} />
        {error && <div style={{ color: '#ff3b30', fontWeight: 600 }}>{error}</div>}
        <button type="submit" style={{ background: `linear-gradient(90deg, ${tokens.accent} 0%, ${tokens.accent2} 100%)`, color: tokens.inverse, border: 'none', borderRadius: 8, padding: '10px 16px', fontWeight: 700, cursor: 'pointer' }}>Accesează</button>
      </form>
    );
  }

  return (
    <div style={{ maxWidth: 960, margin: '32px auto', color: tokens.text, display: 'flex', flexDirection: 'column', gap: 32 }}>
      <div style={{ background: tokens.surface, border: `1px solid ${tokens.border}`, borderRadius: 16, boxShadow: `0 8px 28px ${tokens.shadow}`, padding: 24 }}>
        <h2 style={{ margin: '0 0 8px 0' }}>Setări site - Tema vizuală</h2>
        <p style={{ marginTop: 0, color: tokens.textMuted }}>Alege o temă. Schimbarea se aplică instant pe toate dispozitivele.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginTop: 16 }}>
          {themeList.map((t) => (
            <button key={t.key} onClick={() => saveTheme(t.key)}
              style={{
                textAlign: 'left',
                background: themeKey === t.key ? t.tokens.surface : tokens.surface,
                color: themeKey === t.key ? t.tokens.text : tokens.text,
                border: `2px solid ${themeKey === t.key ? t.tokens.accent : tokens.border}`,
                borderRadius: 12,
                padding: 16,
                cursor: 'pointer',
                boxShadow: `0 4px 20px ${tokens.shadow}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <strong>{t.label}</strong>
                <span style={{ width: 14, height: 14, borderRadius: '50%', background: t.tokens.accent, boxShadow: `0 0 0 3px ${t.tokens.indicatorGlow}` }} />
              </div>
              <div style={{ marginTop: 10, display: 'flex', gap: 6 }}>
                <div title="bg" style={{ width: 22, height: 14, borderRadius: 4, background: t.tokens.bg, border: `1px solid ${t.tokens.border}` }} />
                <div title="surface" style={{ width: 22, height: 14, borderRadius: 4, background: t.tokens.surface, border: `1px solid ${t.tokens.border}` }} />
                <div title="panel" style={{ width: 22, height: 14, borderRadius: 4, background: t.tokens.panel, border: `1px solid ${t.tokens.border}` }} />
                <div title="accent" style={{ width: 22, height: 14, borderRadius: 4, background: t.tokens.accent }} />
                <div title="accent2" style={{ width: 22, height: 14, borderRadius: 4, background: t.tokens.accent2 }} />
              </div>
              <div style={{ marginTop: 10, fontSize: 13, opacity: 0.85 }}>Exemplu buton</div>
              <div style={{ marginTop: 6 }}>
                <div style={{ display: 'inline-block', padding: '8px 12px', borderRadius: 8, background: `linear-gradient(90deg, ${t.tokens.accent} 0%, ${t.tokens.accent2} 100%)`, color: t.tokens.inverse, fontWeight: 700 }}>Previzualizare</div>
              </div>
            </button>
          ))}
        </div>
      </div>
      <div style={{ background: tokens.surface, border: `1px solid ${tokens.border}`, borderRadius: 16, boxShadow: `0 8px 28px ${tokens.shadow}`, padding: 24 }}>
        <h3 style={{ margin: '0 0 12px 0' }}>Opțiuni afișare globale</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <label style={{ fontWeight: 600 }}>Festivitate:</label>
            <select
              value={festiveTheme}
              onChange={(e) => saveDisplayOptions({ festiveTheme: e.target.value })}
              style={{ background: tokens.panel, color: tokens.text, border: `1px solid ${tokens.mutedBorder}`, borderRadius: 8, padding: '8px 12px' }}
            >
              <option value="none">Niciuna</option>
              <option value="christmas">Crăciun</option>
              <option value="halloween">Halloween</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <label style={{ fontWeight: 600 }}>Tile pătrate:</label>
            <input
              type="checkbox"
              checked={squareTiles}
              onChange={(e) => saveDisplayOptions({ squareTiles: e.target.checked })}
            />
            <span style={{ fontSize: 13, color: tokens.textMuted }}>Controlează forma cardurilor (listings, detalii).</span>
          </div>
        </div>
      </div>
      <div style={{ background: tokens.surface, border: `1px solid ${tokens.border}`, borderRadius: 16, boxShadow: `0 8px 28px ${tokens.shadow}`, padding: 24 }}>
        <h3 style={{ margin: '0 0 12px 0' }}>Informații firme & contacte</h3>
        <p style={{ margin: '0 0 18px 0', color: tokens.textMuted, fontSize: 13 }}>Completează datele afișate în footer și pe butoanele flotante. Salvat local (pentru persistare în producție se pot adăuga ulterior API / DB).</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 16 }}>
          {[
            ['companyName','Nume firmă'],
            ['address','Adresă'],
            ['phone','Telefon principal'],
            ['email','Email'],
            ['privacyPolicyUrl','URL Politică confidențialitate'],
            ['termsUrl','URL Termeni & condiții'],
            ['anpcUrl','URL ANPC'],
            ['cui','CUI'],
            ['regCom','Nr. Registrul Comerțului']
          ].map(([key,label]) => (
            <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontWeight: 600, fontSize: 13 }}>{label}</label>
              <input
                value={siteInfo[key] || ''}
                onChange={(e) => updateField(key, e.target.value)}
                placeholder={label}
                style={{ background: tokens.panel, color: tokens.text, border: `1px solid ${tokens.mutedBorder}`, borderRadius: 8, padding: '10px 12px', fontSize: 14 }}
              />
            </div>
          ))}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, gridColumn: '1 / -1' }}>
            <label style={{ fontWeight: 600, fontSize: 13 }}>Program (ex: Luni - Vineri: 09:00-18:00)</label>
            <textarea
              value={siteInfo.hours || ''}
              onChange={(e) => updateField('hours', e.target.value)}
              placeholder="Luni - Vineri: 09:00-18:00&#10;Sâmbătă - Duminică: 10:00-14:00"
              rows={3}
              style={{ background: tokens.panel, color: tokens.text, border: `1px solid ${tokens.mutedBorder}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', resize: 'vertical' }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 20 }}>
          <button onClick={saveSiteInfo} style={{ background: `linear-gradient(90deg, ${tokens.accent}, ${tokens.accent2})`, color: tokens.inverse, border: 'none', borderRadius: 10, padding: '12px 20px', fontWeight: 700, cursor: 'pointer' }}>Salvează datele</button>
          {saveStatus && <span style={{ color: saveStatus.includes('✓') ? '#10b981' : tokens.textMuted, fontWeight: 600 }}>{saveStatus}</span>}
        </div>
      </div>
    </div>
  );
}
